// Instructions pages: scrollspy + reading progress for the "on this page"
// column, copy buttons, image zoom, before/after slider, image tabs,
// looping clips, ⌘K search and the mobile sections drawer. Plain DOM — the page itself
// is server-rendered; React only mounts the shared header and footer.

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
// a few chrome strings in the page language (config/locales, instructions.*)
const T = JSON.parse(document.getElementById("site-chrome")?.dataset.docI18n || "{}");

function setupScrollspy() {
  const links = $$(".doc-toc-list a").filter((a) => a.closest(".doc-toc"));
  if (!links.length) return;
  const targets = links.map((a) => document.getElementById(decodeURIComponent(a.hash.slice(1))));
  const bar = $(".doc-progress div");
  const article = $(".doc-article");
  let ticking = false;

  const update = () => {
    ticking = false;
    const offset = 140;
    let current = 0;
    targets.forEach((t, i) => { if (t && t.getBoundingClientRect().top < offset) current = i; });
    // at the very bottom the last short sections can never reach the offset
    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) current = targets.length - 1;
    links.forEach((a, i) => a.classList.toggle("is-active", i === current));
    if (bar && article) {
      const r = article.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (window.innerHeight * 0.35 - r.top) / r.height));
      bar.style.transform = `scaleX(${p})`;
    }
  };
  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll);
  update();
}

function flash(button, text) {
  const original = button.dataset.label || button.textContent;
  button.dataset.label = original;
  button.textContent = text;
  clearTimeout(button._t);
  button._t = setTimeout(() => { button.textContent = original; }, 1600);
}

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

function setupCopy() {
  $$("[data-copy]").forEach((btn) => btn.addEventListener("click", async () => {
    const body = btn.closest(".doc-prompt")?.querySelector(".doc-prompt-body");
    if (body && await copyText(body.textContent.trim())) flash(btn, T.copied || "Copied");
  }));
  $$("[data-copy-link]").forEach((btn) => btn.addEventListener("click", async () => {
    if (await copyText(location.href.split("#")[0])) flash(btn, T.link_copied || "Link copied");
  }));
  // heading anchors also copy their link
  $$(".doc-anchor").forEach((a) => a.addEventListener("click", () => {
    copyText(new URL(a.getAttribute("href"), location.href).href);
  }));
  $$('a[href="#top"]').forEach((a) => a.addEventListener("click", (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    history.replaceState(null, "", location.pathname);
  }));
}

function setupLightbox() {
  const box = $(".doc-lightbox");
  if (!box) return;
  const img = $("img", box);
  let clip = null;
  $$(".doc-zoom").forEach((btn) => btn.addEventListener("click", () => {
    const src = $("img, video", btn);
    if (!src) return;
    clip?.remove();
    clip = null;
    img.hidden = src.tagName === "VIDEO";
    if (src.tagName === "VIDEO") {
      clip = src.cloneNode();
      clip.muted = true;
      clip.currentTime = src.currentTime;
      box.append(clip);
      clip.play().catch(() => {});
    } else {
      img.src = src.currentSrc || src.src;
      img.alt = src.alt;
    }
    box.showModal();
  }));
  box.addEventListener("click", () => box.close());
  box.addEventListener("close", () => { clip?.remove(); clip = null; });
}

function setupCompare() {
  $$("[data-compare]").forEach((fig) => {
    const range = $("input[type=range]", fig);
    const apply = () => fig.style.setProperty("--pos", `${range.value}%`);
    range.addEventListener("input", apply);
    apply();
  });
}

function setupTabs() {
  $$("[data-tabs]").forEach((group) => {
    const tabs = $$("[role=tab]", group);
    const select = (tab) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute("aria-selected", String(on));
        t.tabIndex = on ? 0 : -1;
        document.getElementById(t.getAttribute("aria-controls")).hidden = !on;
      });
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener("click", () => select(tab));
      tab.addEventListener("keydown", (e) => {
        const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
        if (!step) return;
        const next = tabs[(i + step + tabs.length) % tabs.length];
        select(next);
        next.focus();
      });
    });
  });
}

// Clips loop like GIFs, but only while on screen; with reduced motion
// they stay paused on the first frame and get native controls instead.
function setupClips() {
  const clips = $$(".doc-article video");
  if (!clips.length) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    clips.forEach((v) => { v.removeAttribute("autoplay"); v.pause(); v.controls = true; });
    return;
  }
  const io = new IntersectionObserver((entries) => entries.forEach(({ target, isIntersecting }) => {
    if (isIntersecting && !target.closest("[hidden]")) target.play().catch(() => {});
    else target.pause();
  }), { rootMargin: "120px" });
  clips.forEach((v) => { v.muted = true; io.observe(v); });
  // a clip on a tab that was hidden starts when its tab opens
  $$("[data-tabs] [role=tab]").forEach((tab) => tab.addEventListener("click", () => {
    $$("video", document.getElementById(tab.getAttribute("aria-controls"))).forEach((v) => v.play().catch(() => {}));
  }));
}

function setupDrawer() {
  const side = $(".doc-side");
  const btn = $("[data-nav-open]");
  if (!side || !btn) return;
  const set = (open) => {
    side.classList.toggle("is-open", open);
    btn.setAttribute("aria-expanded", String(open));
    document.documentElement.classList.toggle("doc-lock", open);
  };
  btn.addEventListener("click", () => set(!side.classList.contains("is-open")));
  $$("a", side).forEach((a) => a.addEventListener("click", () => set(false)));
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") set(false); });
}

const normalize = (s) => s.toLowerCase().replace(/ё/g, "е");

function setupSearch() {
  const box = $(".doc-searchbox");
  const data = $("#doc-search-index");
  if (!box || !data) return;
  const index = JSON.parse(data.textContent || "[]");
  const input = $("input", box);
  const list = $(".doc-searchbox-results", box);
  let active = 0;

  const escapeHtml = (s) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const snippet = (text, q) => {
    const i = normalize(text).indexOf(q);
    if (i < 0) return "";
    const start = Math.max(0, i - 50);
    const raw = (start ? "…" : "") + text.slice(start, i + q.length + 70) + "…";
    const k = normalize(raw).indexOf(q);
    return escapeHtml(raw.slice(0, k)) + "<mark>" + escapeHtml(raw.slice(k, k + q.length)) + "</mark>" + escapeHtml(raw.slice(k + q.length));
  };

  const search = (query) => {
    const q = normalize(query.trim());
    if (!q) {
      return index.map((doc) => ({ url: doc.url, title: doc.title, meta: doc.section, text: "" }));
    }
    const hits = [];
    index.forEach((doc) => {
      if (normalize(doc.title).includes(q)) hits.push({ score: 3, url: doc.url, title: doc.title, meta: doc.section, text: "" });
      doc.headings.forEach((h) => {
        if (normalize(h.text).includes(q)) hits.push({ score: 2, url: `${doc.url}#${h.id}`, title: h.text, meta: doc.title, text: "" });
      });
      if (!hits.some((h) => h.url.startsWith(doc.url)) && normalize(doc.text).includes(q)) {
        hits.push({ score: 1, url: doc.url, title: doc.title, meta: doc.section, text: snippet(doc.text, q) });
      }
    });
    return hits.sort((a, b) => b.score - a.score).slice(0, 12);
  };

  const render = () => {
    const results = search(input.value);
    active = Math.min(active, Math.max(0, results.length - 1));
    list.innerHTML = results.length
      ? results.map((r, i) => `<li role="option" aria-selected="${i === active}"><a href="${r.url}"><span class="t">${escapeHtml(r.title)}</span><span class="m">${escapeHtml(r.meta || "")}</span>${r.text ? `<span class="s">${r.text}</span>` : ""}</a></li>`).join("")
      : `<li class="empty">${escapeHtml(T.nothing_found || "Nothing found")}</li>`;
  };

  const open = () => { input.value = ""; active = 0; render(); box.showModal(); input.focus(); };
  $$("[data-search-open]").forEach((b) => b.addEventListener("click", open));
  document.addEventListener("keydown", (e) => {
    const typing = /input|textarea|select/i.test(document.activeElement?.tagName || "");
    if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing && !box.open)) {
      e.preventDefault();
      box.open ? box.close() : open();
    }
  });
  input.addEventListener("input", () => { active = 0; render(); });
  input.addEventListener("keydown", (e) => {
    const items = $$("li[role=option]", list);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      active = (active + (e.key === "ArrowDown" ? 1 : -1) + items.length) % Math.max(1, items.length);
      items.forEach((li, i) => li.setAttribute("aria-selected", String(i === active)));
      items[active]?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter") {
      const link = items[active]?.querySelector("a");
      if (link) { e.preventDefault(); box.close(); location.href = link.href; }
    }
  });
  box.addEventListener("click", (e) => { if (e.target === box) box.close(); });
  list.addEventListener("click", (e) => { if (e.target.closest("a")) box.close(); });
}

setupScrollspy();
setupCopy();
setupLightbox();
setupCompare();
setupTabs();
setupClips();
setupDrawer();
setupSearch();
